import type { Scale, ScaleType } from '@tunasong/schemas'
import type { ChordType, ChordVariant } from '../chord/chord-types.js'
import ScaleLib from '../scale/scale.js'

/**
 * Default chord progressions for the Cadence Lab + Melody Writer standalone
 * routes. Per scale type (Bowie-locked, spec §3.3). The progression is built
 * by mapping each step's `degreeIndex` onto `ScaleLib.getNotes(scale)` and
 * applying the explicit quality from the step.
 *
 * Modal vamps mirror Mode Lab's `MODE_VAMPS` so the suite reads as one
 * coherent set of canonical examples.
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md
 */

type Quality = 'major' | 'minor' | 'diminished'

interface ProgressionStep {
  /** 0-indexed into `ScaleLib.getNotes(scale)`. */
  degreeIndex: number
  quality: Quality
}

const QUALITY_VARIANT: Record<Quality, ChordType> = {
  major: '',
  minor: 'm',
  diminished: 'dim',
}

/**
 * Per scale-type step tables.
 *
 *  - major: I – IV – V (3 chords, satisfies Cadence Lab's MIN_CHORDS = 3)
 *  - minor / aeolian: i – ♭VI – ♭VII – i (canonical minor pop loop)
 *  - dorian: i – IV – i – IV
 *  - mixolydian: I – ♭VII – IV – I
 *  - phrygian: i – ♭II – i – ♭II
 *  - lydian: I – II – I – II (II is MAJOR — Lydian's signature)
 *  - locrian: i° – ♭II – ♭III – i° (edge case; Bowie pre-merge review)
 */
const PROGRESSION_STEPS: Partial<Record<ScaleType, ProgressionStep[]>> = {
  major: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 4, quality: 'major' },
  ],
  minor: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 5, quality: 'major' },
    { degreeIndex: 6, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
  ],
  aeolian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 5, quality: 'major' },
    { degreeIndex: 6, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
  ],
  dorian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 3, quality: 'major' },
  ],
  mixolydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 6, quality: 'major' },
    { degreeIndex: 3, quality: 'major' },
    { degreeIndex: 0, quality: 'major' },
  ],
  phrygian: [
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'major' },
    { degreeIndex: 0, quality: 'minor' },
    { degreeIndex: 1, quality: 'major' },
  ],
  lydian: [
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'major' },
    { degreeIndex: 0, quality: 'major' },
    { degreeIndex: 1, quality: 'major' },
  ],
  locrian: [
    { degreeIndex: 0, quality: 'diminished' },
    { degreeIndex: 1, quality: 'major' },
    { degreeIndex: 2, quality: 'major' },
    { degreeIndex: 0, quality: 'diminished' },
  ],
}

const FALLBACK_SCALE: Scale = { root: 'C', type: 'major' }

/**
 * Build the default chord progression for a given scale. Returns a list of
 * `ChordVariant` values (one per progression step). Unknown / unsupported
 * scale types fall back to C major (silent fallback — matches the
 * URL-param parsers' convention).
 */
export const getDefaultProgressionForScale = (scale: Scale): ChordVariant[] => {
  const type: ScaleType | undefined = scale.type
  const steps = type ? PROGRESSION_STEPS[type] : undefined
  if (!steps) {
    // Unknown / unsupported scale type — fall back to C major default.
    const fallbackSteps = PROGRESSION_STEPS.major ?? []
    const notes = ScaleLib.getNotes(FALLBACK_SCALE)
    return fallbackSteps.map((step: ProgressionStep) => ({
      root: notes[step.degreeIndex],
      variant: QUALITY_VARIANT[step.quality],
    }))
  }
  const notes = ScaleLib.getNotes(scale)
  return steps.map((step: ProgressionStep) => ({
    root: notes[step.degreeIndex],
    variant: QUALITY_VARIANT[step.quality],
  }))
}
